import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import type { DocInterpretationRegion } from '../../../../../types/docInterpretation';
import { regionStyle } from './docInterpretationHelpers';
import { PdfTextItem, snapRegionToText, TextAnchor } from './docInterpretationTextSnap';

// Workeren pakkes av webpack som egen fil (asset/resource via new URL).
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.js', import.meta.url).toString();

interface PdfPageViewProps {
  /** Originaldokumentet. */
  file: Blob;
  /** Siden som skal vises (1 = første). Følger linjen innkjøper peker på. */
  page: number;
  onPageChange: (page: number) => void;
  /** Linjens område på siden slik AI-en anslo det (punkt 15). Brukes når teksten ikke finnes i PDF-en. */
  highlight: DocInterpretationRegion | null;
  /** Linjens kjennetegn (batch, varenummer, EAN): uthevingen legges på raden der de står i tekstlaget. */
  anchors?: TextAnchor[];
}

/**
 * Viser én side av leverandørdokumentet med pdf.js og uthever linjen innkjøper peker på
 * (TW 36423840, punkt 15). Nettleserens egen PDF-visning lar oss ikke legge noe over siden.
 */
export const PdfPageView: React.FC<PdfPageViewProps> = ({ file, page, onPageChange, highlight, anchors }) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  // Sidens tekstlag (posisjon per tekstbit), for å treffe raden eksakt i digitale PDF-er
  const [textItems, setTextItems] = useState<PdfTextItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setPdf(null);
    setError(null);

    file.arrayBuffer()
      .then(buffer => pdfjsLib.getDocument({
        data: new Uint8Array(buffer),
        // Dokumentene kommer fra leverandører: ingen eval i fonttolkingen (CVE-2024-4367, pdfjs-dist < 4.2.67)
        isEvalSupported: false
      }).promise)
      .then(doc => {
        loaded = doc;
        if (!cancelled) setPdf(doc);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
      loaded?.destroy();
    };
  }, [file]);

  const pageCount = pdf?.numPages ?? 0;
  const currentPage = pageCount > 0 ? Math.min(Math.max(page, 1), pageCount) : page;

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<void> } | null = null;
    setRendering(true);

    setTextItems([]);
    pdf.getPage(currentPage).then(pdfPage => {
      if (cancelled || !canvasRef.current) return;

      const textViewport = pdfPage.getViewport({ scale: 1 });
      pdfPage.getTextContent().then(content => {
        if (cancelled) return;
        setTextItems(content.items
          .filter((item): item is typeof item & { str: string; transform: number[]; width: number } => 'str' in item && item.str.trim() !== '')
          .map(item => {
            const tx = pdfjsLib.Util.transform(textViewport.transform, item.transform);
            const height = Math.hypot(tx[2], tx[3]);
            return {
              str: item.str,
              x: tx[4] / textViewport.width,
              top: (tx[5] - height) / textViewport.height,
              width: item.width / textViewport.width,
              height: height / textViewport.height,
            };
          }));
      }).catch(() => undefined);

      const canvas = canvasRef.current;
      const container = canvas.parentElement;
      const unscaled = pdfPage.getViewport({ scale: 1 });
      const width = container?.clientWidth || unscaled.width;
      const scale = (width / unscaled.width) * (window.devicePixelRatio || 1);
      const viewport = pdfPage.getViewport({ scale });

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = '100%';
      canvas.style.height = 'auto';

      const context = canvas.getContext('2d');
      if (!context) return;
      renderTask = pdfPage.render({ canvasContext: context, viewport });
      renderTask.promise
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setRendering(false);
        });
    });

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, currentPage]);

  // Raden der linjens kjennetegn står i tekstlaget; ellers AI-ens anslag
  const anchorsKey = JSON.stringify(anchors ?? []);
  const region = useMemo(
    () => (highlight || anchors?.length ? snapRegionToText(textItems, anchors ?? [], highlight) ?? highlight : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [textItems, anchorsKey, highlight?.x, highlight?.y, highlight?.width, highlight?.height]
  );

  // Rull den uthevede linjen inn i synsfeltet når innkjøper velger en ny linje (punkt 15).
  // Nøkkelen er verdien, ikke objektet — området lages på nytt ved hver render hos forelderen.
  const highlightKey = region ? `${region.x}:${region.y}:${region.width}:${region.height}` : null;
  useEffect(() => {
    if (!rendering && highlightKey && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlightKey, rendering]);

  if (error) {
    return (
      <div className="p-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg">
        {t('manager.modules.workingOrders.docInterpretation.detail.pdfError', { '0': error }, `Kunne ikke vise dokumentet: ${error}`)}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 text-sm text-gray-600">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!pdf || currentPage <= 1}
          className="p-2 rounded hover:bg-gray-100 disabled:text-gray-400"
          aria-label={t('manager.modules.workingOrders.docInterpretation.detail.previousPage', {}, 'Forrige side')}
        >
          <ChevronLeft size={18} />
        </button>
        <span>
          {pdf
            ? t('manager.modules.workingOrders.docInterpretation.detail.pageOf', { '0': currentPage, '1': pageCount }, `Side ${currentPage} av ${pageCount}`)
            : t('manager.modules.workingOrders.docInterpretation.detail.loadingDocument', {}, 'Laster dokumentet...')}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pdf || currentPage >= pageCount}
          className="p-2 rounded hover:bg-gray-100 disabled:text-gray-400"
          aria-label={t('manager.modules.workingOrders.docInterpretation.detail.nextPage', {}, 'Neste side')}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="relative flex-1 overflow-auto bg-gray-100 p-2">
        <div className="relative bg-white shadow">
          <canvas ref={canvasRef} className="block" />
          {region && !rendering && (
            <div
              ref={highlightRef}
              className="absolute border-2 border-blue-500 bg-blue-50 rounded-sm pointer-events-none"
              style={{ ...regionStyle(region), mixBlendMode: 'multiply' }}
              data-testid="doc-line-highlight"
            />
          )}
          {(!pdf || rendering) && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader className="animate-spin text-gray-400" size={24} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
